import React from 'react';
import styled from 'styled-components';

/**
 * LEGACY inline spinner — closed to new usage. See docs/design-system.md →
 * "Loading & Progress" → "Migration note". Use `LoadingState` from
 * `common/Spinner` instead.
 *
 * `icon` used to default to the four-leaf-clover `FeelingLuckyIcon`, a leftover
 * from the AI-image feature the ring was written for. Eleven call sites never
 * overrode it — including the app's very first paint ("Signing you in…"), which
 * made the clover the most-seen loading glyph in the product. It now defaults
 * to nothing, so a caller renders only what it asks for.
 */
type Props = {
  icon?: React.ReactNode;
  text?: string | React.ReactNode;
  iconSize?: number;
  spinnerSize?: number;
  spinnerWidth?: number;
};

const SimpleLoadingSpinner = ({
  icon,
  text = 'Loading…',
  iconSize,
  spinnerSize,
  spinnerWidth,
}: Props) => {
  return (
    <Content data-testid="loading-spinner">
      <SpinnerWrapper size={spinnerSize}>
        {icon && <Icon size={iconSize}>{icon}</Icon>}
        <Spinner spinnerWidth={spinnerWidth}></Spinner>
      </SpinnerWrapper>
      {text && <Text>{text}</Text>}
    </Content>
  );
};

export default SimpleLoadingSpinner;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;
`;

// Matches LoadingMessage in common/Spinner — the canonical loading label.
const Text = styled.p`
  margin: 0;
  color: var(--foreground);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semi-bold);
  text-align: center;
`;

const SpinnerWrapper = styled.div<{ size?: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: ${props => props.size || 80}px;
  height: ${props => props.size || 80}px;
  position: relative;
`;

const Spinner = styled.div<{ spinnerWidth?: number }>`
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  border: ${props => props.spinnerWidth || 5}px solid var(--border);
  border-top: ${props => props.spinnerWidth || 5}px solid var(--primary);
  border-left: ${props => props.spinnerWidth || 5}px solid var(--primary);
  border-right: ${props => props.spinnerWidth || 5}px solid var(--primary);
  border-radius: 50%;
  animation: spin 2s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const Icon = styled.div<{ size?: number }>`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${props => props.size || 25}px;
  height: ${props => props.size || 24}px;
`;
